import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { KeenIcon } from '@/components/keenicons';
import { useUpdateRole } from '@/hooks/admin/useRoles';
import type { Role, UpdateRoleRequest } from '@/services/admin/role.types';
import { toast } from 'sonner';
import { format } from 'date-fns';

const updateRoleSchema = z.object({
  name: z
    .string()
    .min(1, 'Role name is required')
    .max(256, 'Role name cannot exceed 256 characters')
    .regex(
      /^[a-zA-Z0-9_\-\s.]+$/,
      'Role name can only contain letters, numbers, spaces, hyphens, underscores, and dots'
    ),
  description: z
    .string()
    .max(500, 'Description cannot exceed 500 characters')
    .optional(),
  isDefault: z.boolean(),
  isPublic: z.boolean(),
});

type UpdateRoleFormData = z.infer<typeof updateRoleSchema>;

interface RoleDetailsOverviewProps {
  role: Role;
  isEditMode: boolean;
  onEditComplete: () => void;
}

/**
 * Role Details Overview Tab
 *
 * Displays and allows editing of role information
 *
 * Features:
 * - View/edit role name and description
 * - Toggle default and public flags
 * - Display role metadata (creation date, ID, etc.)
 * - Statistics cards
 *
 * Spec reference: Lines 283-298 in 03-AUTH-AUTHORIZATION-SPECS.md
 */
export function RoleDetailsOverview({
  role,
  isEditMode,
  onEditComplete,
}: RoleDetailsOverviewProps) {
  const updateRoleMutation = useUpdateRole();

  const form = useForm<UpdateRoleFormData>({
    resolver: zodResolver(updateRoleSchema),
    defaultValues: {
      name: role.name,
      description: (role.extraProperties?.description as string) || '',
      isDefault: role.isDefault,
      isPublic: role.isPublic,
    },
  });

  const onSubmit = async (data: UpdateRoleFormData) => {
    try {
      const updateData: UpdateRoleRequest = {
        name: data.name.trim(),
        isDefault: data.isDefault,
        isPublic: data.isPublic,
        concurrencyStamp: role.concurrencyStamp,
        extraProperties: {
          ...role.extraProperties,
          description: data.description?.trim() || '',
        },
      };

      await updateRoleMutation.mutateAsync({
        id: role.id,
        data: updateData,
      });

      toast.success('Role updated successfully', {
        description: `The role "${data.name}" has been updated.`,
      });

      onEditComplete();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      toast.error('Failed to update role', {
        description: message,
      });
    }
  };

  if (!isEditMode) {
    return (
      <div className="space-y-6">
        {/* Role Information Card */}
        <Card>
          <CardHeader>
            <CardTitle>Role Information</CardTitle>
            <CardDescription>Basic details about this role</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Role Name</label>
                <p className="mt-1 text-base font-semibold">{role.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Role ID</label>
                <p className="mt-1 font-mono text-sm text-muted-foreground">{role.id}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1 text-base">
                {(role.extraProperties?.description as string) || (
                  <span className="italic text-muted-foreground">No description provided</span>
                )}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={role.isDefault} disabled />
                <span className="text-sm">Assign to new users automatically</span>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={role.isPublic} disabled />
                <span className="text-sm">Publicly available for selection</span>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={role.isStatic} disabled />
                <span className="text-sm">System role (cannot be deleted)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            title="Permissions"
            value="—"
            description="Permissions assigned"
            icon="shield-tick"
            iconColor="text-blue-600"
            bgColor="bg-blue-50"
          />
          <StatCard
            title="Users"
            value="—"
            description="Users with this role"
            icon="profile-circle"
            iconColor="text-green-600"
            bgColor="bg-green-50"
          />
          <StatCard
            title="Created"
            value={role.extraProperties?.creationTime ? format(new Date(role.extraProperties.creationTime as string), 'MMM d, yyyy') : '—'}
            description="Role creation date"
            icon="calendar"
            iconColor="text-purple-600"
            bgColor="bg-purple-50"
          />
        </div>

        {/* Metadata Card */}
        <Card>
          <CardHeader>
            <CardTitle>Metadata</CardTitle>
            <CardDescription>Technical information about this role</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-sm text-muted-foreground">Concurrency Stamp</span>
              <span className="font-mono text-sm">{role.concurrencyStamp}</span>
            </div>
            {role.extraProperties && Object.keys(role.extraProperties).length > 1 && (
              <div>
                <span className="text-sm font-medium text-muted-foreground">Extra Properties</span>
                <pre className="mt-2 overflow-auto rounded-md bg-muted p-3 text-xs">
                  {JSON.stringify(role.extraProperties, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Edit Mode
  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Role Information</CardTitle>
        <CardDescription>
          Modify role details. Changes will affect all users assigned to this role.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Role Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Role Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={role.isStatic || updateRoleMutation.isPending}
                    />
                  </FormControl>
                  {role.isStatic && (
                    <FormDescription className="text-yellow-600">
                      ⚠️ System roles cannot be renamed
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      disabled={updateRoleMutation.isPending}
                    />
                  </FormControl>
                  <FormDescription>
                    Describe the purpose and responsibilities of this role
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Options */}
            <div className="space-y-4 rounded-lg border p-4">
              <h4 className="text-sm font-semibold">Role Options</h4>

              <FormField
                control={form.control}
                name="isDefault"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={updateRoleMutation.isPending}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Set as default role</FormLabel>
                      <FormDescription>
                        Automatically assign this role to new users
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isPublic"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={updateRoleMutation.isPending}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Make publicly available</FormLabel>
                      <FormDescription>
                        Allow users to see and potentially request this role
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={updateRoleMutation.isPending}
                className="gap-2"
              >
                {updateRoleMutation.isPending && (
                  <KeenIcon icon="arrows-circle" className="animate-spin" />
                )}
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onEditComplete}
                disabled={updateRoleMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

/**
 * Stat Card Component
 * Displays a metric with icon
 */
interface StatCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: string;
  iconColor: string;
  bgColor: string;
}

function StatCard({ title, value, description, icon, iconColor, bgColor }: StatCardProps) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 pt-6">
        <div className={`rounded-lg p-3 ${bgColor}`}>
          <KeenIcon icon={icon} className={`text-2xl ${iconColor}`} />
        </div>
        <div className="flex-1">
          <p className="text-xs font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}
